import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * React 19 Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details for debugging
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({
            error,
            errorInfo
        });

        // You could also log to an error reporting service here
        // Example: logErrorToService(error, errorInfo);
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    handleGoHome = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            // Custom error UI
            return (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                    <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8">
                        {/* Header */}
                        <div className="flex items-center justify-center mb-6">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-8 h-8 text-red-600" />
                            </div>
                        </div>

                        {/* Title */}
                        <h1 className="text-3xl font-bold text-gray-900 text-center mb-4">
                            Oups! Une erreur s'est produite
                        </h1>

                        {/* Description */}
                        <p className="text-gray-600 text-center mb-6">
                            Quelque chose s'est mal passé. Nos ingénieurs ont été notifiés et travaillent sur une solution.
                        </p>

                        {/* Error details (only in development) */}
                        {import.meta.env.DEV && this.state.error && (
                            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
                                <h2 className="text-sm font-semibold text-red-800 mb-2">
                                    Détails de l'erreur (mode développement):
                                </h2>
                                <pre className="text-xs text-red-700 overflow-x-auto whitespace-pre-wrap">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={this.handleReset}
                                className="bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                            >
                                <RefreshCw className="w-5 h-5" />
                                Réessayer
                            </button>
                            <button
                                onClick={this.handleGoHome}
                                className="bg-white text-linkoo-purple border-2 border-linkoo-purple px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                            >
                                <Home className="w-5 h-5" />
                                Retour à l'accueil
                            </button>
                        </div>

                        {/* Additional help */}
                        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                            <p className="text-sm text-gray-500">
                                Si le problème persiste, veuillez contacter le support technique.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * Functional wrapper for ErrorBoundary with custom fallback
 * This allows you to use custom error UI if needed
 */
export const withErrorBoundary = (Component, customFallback) => {
    return (props) => (
        <ErrorBoundary fallback={customFallback}>
            <Component {...props} />
        </ErrorBoundary>
    );
};

export default ErrorBoundary;
